import React, { useCallback, useEffect, useMemo } from "react";

// types
import { Location } from "@/types/trip";

// stores and hooks
import useTripStore from "@/stores/tripBuilder/useTrip";
import useLocationManager from "@/hooks/useLocationManager";
import { useTransportManager } from "@/utils/useTransportManager";
import useTransportStore from "@/stores/tripBuilder/useTransport";
import useView from "@/stores/tripBuilder/useView";
import { useTripBuilderState } from "@/hooks/useTripBuilderState";
import { useTripRealtime } from "@/hooks/useTripRealtime";

// components
import PermissionDialog from "@/contexts/PermissionDialogContext";
import TripHeader from "@tripbuilder/trip-header";
import TripSidebar from "@tripbuilder/location-sidebar/TripSidebar";
import TripMiddleContent from "@tripbuilder/main/TripMiddleContent";
import TripRightContent from "@tripbuilder/main/TripRightContent";
import TripLoader from "@tripbuilder/loading-components/TripLoader";

const TripBuilder: React.FC = () => {
  // Custom hook to manage trip builder state - use selective state extraction
  const {
    isAddDestinationModalOpen: isModalOpen,
    tripLoading,
    tripId,
    tripData,
    setModalOpen,
    cleanupTripBuilder,
  } = useTripBuilderState();

  // Initialize real-time functionality
  useTripRealtime(tripId || "");

  // Extract only what's needed from stores to minimize renders
  const transportData = useTransportStore((state) => state.transportData);
  const activeTransportButtonId = useTransportStore(
    (state) => state.activeTransportButtonId
  );
  const setTransportData = useTransportStore((state) => state.setTransportData);
  const setActiveTransportButtonId = useTransportStore(
    (state) => state.setActiveTransportButtonId
  );

  const selectedLocation = useTripStore((state) => state.selectedLocation);

  const {
    flightModalOpen,
    setFlightModalOpen,
    selectedFlight,
    setSelectedFlight,
    addedFlight,
    addOrReplaceFlightInTrip,
    deleteFlightFromTrip,
  } = useTransportManager(useTripStore.getState().updateTripData);

  // Memoize handlers to prevent unnecessary recreation
  const handleTransportButtonClick = useCallback(
    (clickedLocation: Location) => {
      if (!tripData) return;

      const selectedIndex = tripData.locations.findIndex(
        (loc: Location) => loc.id === clickedLocation.id && !loc.origin
      );

      if (selectedIndex === -1) return;

      const locationAbove =
        selectedIndex > 0 ? tripData.locations[selectedIndex - 1] : null;
      const locationBelow = tripData.locations[selectedIndex];

      setTransportData({
        selectedLocation: clickedLocation,
        locationAbove: locationAbove as Location,
        locationBelow: locationBelow as Location,
      });
      setActiveTransportButtonId(clickedLocation.id);

      // Use a functional update to avoid dependency on the store reference
      const viewStore = useView.getState();
      viewStore.setPanel("transport");
    },
    [tripData, setTransportData, setActiveTransportButtonId]
  );

  const handleSwitchToSchedule = useCallback(() => {
    const viewStore = useView.getState();
    viewStore.setActiveTab("schedule");
  }, []);

  // Cleanup effect
  useEffect(() => {
    return cleanupTripBuilder;
  }, [cleanupTripBuilder]);

  // Memoize the plan view to prevent unnecessary re-renders
  const planView = useMemo(() => {
    if (!tripData || !selectedLocation) {
      return null;
    }

    return (
      <section className="bg-white max-h-dvh w-full flex flex-row items-start overflow-hidden">
        <div className="min-w-[56.5%] w-[56.5%] max-w-[56.5%] min-h-full h-full flex flex-col items-start overflow-hidden">
          <TripHeader />
          <div className="w-full h-full min-h-full flex flex-row items-start justify-between">
            <TripSidebar
              isModalOpen={isModalOpen}
              transportData={transportData}
              activeTransportButtonId={activeTransportButtonId}
              handleTransportButtonClick={handleTransportButtonClick}
              setIsModalOpen={setModalOpen}
            />
            <TripMiddleContent
              setSelectedFlight={setSelectedFlight}
              selectedFlight={selectedFlight}
              deleteFlightFromTrip={deleteFlightFromTrip}
              setFlightModalOpen={setFlightModalOpen}
            />
          </div>
        </div>
        <TripRightContent
          switchToSchedule={handleSwitchToSchedule}
          tripData={tripData}
          isAddDestinationModalOpen={isModalOpen}
          transportData={transportData}
          selectedFlight={selectedFlight}
          setSelectedFlight={setSelectedFlight}
          addFlightToTrip={addOrReplaceFlightInTrip}
          flightModalOpen={flightModalOpen}
          setFlightModalOpen={setFlightModalOpen}
          handleTransportButtonClick={handleTransportButtonClick}
          setIsModalOpen={setModalOpen}
        />
        <PermissionDialog />
        {/* <BookingModal */}
      </section>
    );
  }, [
    isModalOpen,
    transportData,
    activeTransportButtonId,
    handleTransportButtonClick,
    setModalOpen,
    selectedFlight,
    setSelectedFlight,
    deleteFlightFromTrip,
    setFlightModalOpen,
    handleSwitchToSchedule,
    tripData,
    flightModalOpen,
    addOrReplaceFlightInTrip,
    selectedLocation,
  ]);

  // Loading state
  if (
    tripLoading ||
    tripId !== tripData?.id ||
    !tripData ||
    !selectedLocation
  ) {
    return <TripLoader />;
  }

  return <main>{planView}</main>;
};

// Wrap the entire component in React.memo
export default React.memo(TripBuilder);
